// Mirrors hp-ai-service's frozen wire contract (design-spec §7) and
// prowl-components/src/contracts/prowl-protocol.ts. Each consumer vendors its
// own copy by convention (see that file's own header comment) — this repo
// cannot import either sibling repo directly. Keep in sync by hand.

export type Persona = 'customer' | 'provider';
export type Locale = 'en' | 'fr';

// The one route pageContext.route must equal for search_services to emit
// `result_set` instead of a ServiceCardList component. Re-ported verbatim
// from ai-work/docs/handoff/prowl/contracts/prowl-protocol.ts — every
// pageContext this app sends while claiming to be "on the results page"
// must use this constant, never a re-typed literal, or hp-ai-service's route
// match silently fails and the grid keeps running its own search.
export const PROWL_RESULTS_ROUTE = '/services/search-list';

export interface ProwlPageContext {
  route?: string;
  entityId?: string;
}

export interface ProwlAttachment {
  type: 'image';
  s3Key: string;
}

export interface ProwlRequest {
  sessionId: string | null;
  message: string;
  persona: Persona;
  pageContext?: ProwlPageContext;
  attachments?: ProwlAttachment[];
}

export type ComponentType =
  | 'ServiceCardList'
  | 'SlotPicker'
  | 'IntakeForm'
  | 'BookingDraftCard'
  | 'QuoteRequestDraftCard'
  | 'PriceRangeBand'
  | 'ServiceDraftEditor'
  | 'ProfileChecklist'
  | 'NavigateChip'
  | 'ConfirmAction'
  | 'EscalationCard';

// Frontend resolves these to endpoints (design-spec §5). The model never
// emits a URL — see src/lib/prowl/actionAllowlist.ts.
export type ActionKey =
  | 'createBooking'
  | 'createQuoteRequest'
  | 'publishService'
  | 'saveServiceDraft';

export type ToolStatus = 'ok' | 'error' | 'timeout';

export type ProwlBookingType = 'SLOT' | 'PACKAGE' | 'QUOTE';
export type ProwlUrgency = 'low' | 'medium' | 'high';

export interface ProwlInterpretedQuery {
  keywords: string[];
  categoryId: string | null;
  categoryName: string | null;
  city: string | null;
  bookingType: ProwlBookingType | null;
  urgency: ProwlUrgency | null;
  date: string | null; // ISO date, or null
}

export interface ProwlResultSetService {
  serviceId: string;
  serviceTitle: string;
  slug: string;
  categoryId: string;
  categoryName: string;
  subCategoryId: string | null;
  bookingType: ProwlBookingType;
  price: number | null;
  offerPrice: number | null;
  isOffers: boolean;
  businessId: string;
  businessName: string;
}

export interface ProwlResultSetData {
  queryId: string;
  interpretedQuery: ProwlInterpretedQuery;
  services: ProwlResultSetService[];
  total: number;
}

export type ProwlEvent =
  | { event: 'session'; data: { sessionId: string } }
  | { event: 'text_delta'; data: { text: string } }
  | {
      event: 'tool_started';
      data: { toolCallId: string; name: string; label: string };
    }
  | {
      event: 'tool_finished';
      data: { toolCallId: string; status: ToolStatus; durationMs: number };
    }
  | {
      event: 'component';
      data: {
        componentId: string;
        type: ComponentType | string;
        props: unknown;
      };
    }
  | {
      event: 'confirm_request';
      data: {
        componentId: string;
        actionKey: ActionKey | string;
        payload: unknown;
      };
    }
  | { event: 'escalation'; data: { ref: string; channel: 'email' } }
  | {
      event: 'error';
      data: { code: string; message: string; retryable: boolean };
    }
  | { event: 'done'; data: { turnId: string; truncated: boolean } }
  // Emitted only when pageContext.route is the services results page
  // (design-spec §7 / prowl-overnight-batch.md "FROZEN CONTRACT"). On that
  // route this REPLACES the ServiceCardList component event — the grid
  // renders this instead of running its own query (one search, one grid).
  // Everywhere else search_services keeps emitting ServiceCardList as today.
  | { event: 'result_set'; data: ProwlResultSetData };
